import { LocalPort, LocalPortCall } from './localAolGenerator';
import { TankersEvent } from '../../types/ViewModel/TankersEvent';

interface PortCallData {
    portClusterId: number | null;
    portClusterCallId: number;
    port: LocalPort | undefined;
    portCallId: number;
    voyageNumber: string | null;
}

interface PortClusterCallData {
    portCallIdList: number[];
    portId: number;
    port: LocalPort | undefined;
}

/**
 * Result of computing cluster data - immutable data structure
 */
export interface ClusterComputationResult {
    portCallToCluster: ReadonlyMap<number, PortCallData>;
    clusterToPortCalls: ReadonlyMap<number, PortClusterCallData>;
}

/**
 * Computes port cluster call IDs based on business rules.
 * Pure function - no side effects, returns immutable result.
 * 
 * Business Rules:
 * - First port call gets portClusterCallId = 1
 * - Subsequent port calls get same ID if:
 *   - Same portClusterId OR
 *   - Same non-empty portClusterName across different ports OR
 *   - Turnaround port (same port, different voyage) OR
 *   - Same port with no events
 * - Otherwise, increment portClusterCallId
 */
export const computeClusterData = (
    portCalls: readonly LocalPortCall[],
    events: readonly TankersEvent[]
): ClusterComputationResult => {
    if (portCalls.length === 0) {
        return {
            portCallToCluster: new Map(),
            clusterToPortCalls: new Map(),
        };
    }

    // Build event presence lookup
    const eventsPerPortCall = buildEventPresenceSet(events);

    // Initialize cache from port calls
    const portCallCache = initializePortCallCache(portCalls);

    // Compute portClusterCallId values sequentially
    const enrichedCache = computePortClusterCallIds(portCallCache, eventsPerPortCall);

    // Build reverse index (portClusterCallId -> port call IDs)
    const clusterCache = buildClusterToPortCallsIndex(enrichedCache);

    return {
        portCallToCluster: enrichedCache,
        clusterToPortCalls: clusterCache,
    };
};

/**
 * Looks up cluster data for a specific port call ID.
 */
export const getPortCluster = (
    result: ClusterComputationResult,
    portCallId: number
): PortCallData | undefined => {
    return result.portCallToCluster.get(portCallId);
};

/**
 * Gets all port call IDs that belong to a specific cluster call ID.
 */
export const getPortCallIdsForClusterCallId = (
    result: ClusterComputationResult,
    portClusterCallId: number
): number[] | undefined => {
    return result.clusterToPortCalls.get(portClusterCallId)?.portCallIdList;
};

// ============================================================================
// Private helper functions
// ============================================================================

const buildEventPresenceSet = (events: readonly TankersEvent[]): ReadonlySet<number> => {
    const eventPresence = new Set<number>();
    for (const ev of events) {
        if (ev.portCallId !== null && ev.portCallId !== undefined) {
            eventPresence.add(ev.portCallId);
        }
    }
    return eventPresence;
};

const initializePortCallCache = (
    portCalls: readonly LocalPortCall[]
): Map<number, PortCallData> => {
    const cache = new Map<number, PortCallData>();

    for (const pc of portCalls) {
        const portCallId = pc.imosPortCallId;
        cache.set(portCallId, {
            portClusterId: pc.portClusterId ?? null,
            portClusterCallId: 0, // Will be computed
            port: pc.port,
            portCallId: portCallId,
            voyageNumber: pc.voyageNumber ?? null,
        });
    }

    return cache;
};

const computePortClusterCallIds = (
    cache: Map<number, PortCallData>,
    eventsPerPortCall: ReadonlySet<number>
): Map<number, PortCallData> => {
    const entries = Array.from(cache.values());

    if (entries.length === 0) {
        return cache;
    }

    // First port call always gets portClusterCallId = 1
    entries[0].portClusterCallId = 1;

    // Process pairs of consecutive port calls
    for (let i = 1; i < entries.length; i++) {
        const prevData = entries[i - 1];
        const currData = entries[i];

        currData.portClusterCallId = getPortClusterCallIdForSecondPortCall(
            prevData,
            currData,
            eventsPerPortCall
        );
    }

    return cache;
};

const getPortClusterCallIdForSecondPortCall = (
    portCallData1: PortCallData,
    portCallData2: PortCallData,
    eventsPerPortCall: ReadonlySet<number>
): number => {
    const inSameCluster = areInSameCluster(portCallData1, portCallData2);
    const meetsPortCondition = satisfiesPortCondition(
        portCallData1,
        portCallData2,
        eventsPerPortCall
    );

    if (inSameCluster && meetsPortCondition) {
        return portCallData1.portClusterCallId; // Same cluster
    } else {
        return portCallData1.portClusterCallId + 1; // New cluster
    }
};

const areInSameCluster = (
    portCallData1: PortCallData,
    portCallData2: PortCallData
): boolean => {
    // A populated numeric cluster ID remains authoritative when both calls have one.
    const port1ClusterId = portCallData1.portClusterId ?? portCallData1.port?.portClusterId;
    const port2ClusterId = portCallData2.portClusterId ?? portCallData2.port?.portClusterId;
    if (
        hasNumericClusterId(port1ClusterId) &&
        hasNumericClusterId(port2ClusterId)
    ) {
        if (port1ClusterId === port2ClusterId) return true;

        const port1Id = portCallData1.port?.portId;
        const port2Id = portCallData2.port?.portId;
        if (port1Id !== null && port1Id !== undefined && port2Id !== null && port2Id !== undefined && port1Id !== port2Id) {
            return false;
        }
    }

    // Rule 3: Same non-empty cluster name from the port master data.
    const port1ClusterName = normalizeClusterName(portCallData1.port?.portClusterName);
    const port2ClusterName = normalizeClusterName(portCallData2.port?.portClusterName);
    if (port1ClusterName !== null && port1ClusterName === port2ClusterName) {
        return true;
    }

    // Rule 4: Turnaround port (same port, different voyages)
    const port1Id = portCallData1.port?.portId;
    const port2Id = portCallData2.port?.portId;
    if (
        port1Id !== null &&
        port1Id !== undefined &&
        port1Id === port2Id &&
        portCallData1.voyageNumber !== portCallData2.voyageNumber
    ) {
        return true;
    }

    return false;
};

const hasNumericClusterId = (clusterId: number | null | undefined): boolean =>
    clusterId !== null && clusterId !== undefined;

const normalizeClusterName = (clusterName: string | null | undefined): string | null => {
    const normalizedName = clusterName?.trim() ?? '';
    return normalizedName.length > 0 ? normalizedName : null;
};

const satisfiesPortCondition = (
    portCallData1: PortCallData,
    portCallData2: PortCallData,
    eventsPerPortCall: ReadonlySet<number>
): boolean => {
    return (
        portIsDifferent(portCallData1, portCallData2) ||
        isSamePortButNoEvents(portCallData1, portCallData2, eventsPerPortCall)
    );
};

const portIsDifferent = (
    portCallData1: PortCallData,
    portCallData2: PortCallData
): boolean => {
    const port1Id = portCallData1.port?.portId;
    const port2Id = portCallData2.port?.portId;

    if (port1Id === null || port1Id === undefined) return true;
    if (port2Id === null || port2Id === undefined) return true;

    return port1Id !== port2Id;
};

const isSamePortButNoEvents = (
    portCallData1: PortCallData,
    portCallData2: PortCallData,
    eventsPerPortCall: ReadonlySet<number>
): boolean => {
    const port1Id = portCallData1.port?.portId;
    const port2Id = portCallData2.port?.portId;

    return (
        port1Id !== null &&
        port1Id !== undefined &&
        port1Id === port2Id &&
        !eventsPerPortCall.has(portCallData2.portCallId)
    );
};

const buildClusterToPortCallsIndex = (
    portCallCache: ReadonlyMap<number, PortCallData>
): Map<number, PortClusterCallData> => {
    const clusterCache = new Map<number, PortClusterCallData>();

    // Use Array.from for downlevel compatibility
    const entries = Array.from(portCallCache.entries());
    for (let i = 0; i < entries.length; i++) {
        const [portCallId, data] = entries[i];
        const portClusterCallId = data.portClusterCallId;

        if (!clusterCache.has(portClusterCallId)) {
            clusterCache.set(portClusterCallId, {
                portCallIdList: [],
                portId: data.port?.portId ?? 0,
                port: data.port,
            });
        }

        clusterCache.get(portClusterCallId)!.portCallIdList.push(portCallId);
    }

    return clusterCache;
};
